import type { Expense } from "@haulsheet/core";

/**
 * Calendar date (YYYY-MM-DD) "now" in the org's timezone.
 * `toISOString().slice(0, 10)` is the UTC date: a 7 PM fill-up in Denver
 * would be logged as tomorrow and land in the wrong settlement week.
 */
export function localDateIso(timeZone?: string | null, now: Date = new Date()): string {
  const fmt = (tz?: string) =>
    new Intl.DateTimeFormat("en-CA", {
      timeZone: tz,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(now);
  if (timeZone) {
    try {
      return fmt(timeZone);
    } catch {
      /* unknown zone → device zone */
    }
  }
  return fmt(undefined);
}

export interface FuelFormInput {
  gallons: string;
  price: string;
  occurredOn: string;
  where: string;
}

export type FuelValues = Pick<
  Expense,
  "gallons" | "priceMilliCents" | "amountCents" | "occurredOn" | "note"
>;

function num(s: string): number {
  const t = s.replace(/[$,\s]/g, "");
  return t === "" ? Number.NaN : Number(t);
}

/** Validate the fuel form. Money math stays in integers. */
export function parseFuelForm(
  input: FuelFormInput,
): { ok: true; value: FuelValues } | { ok: false; message: string } {
  const g = num(input.gallons);
  const p = num(input.price);
  if (!Number.isFinite(g) || g <= 0) return { ok: false, message: "Enter gallons" };
  if (g > 500) return { ok: false, message: "Gallons looks too high (max 500)" };
  if (!Number.isFinite(p) || p <= 0) return { ok: false, message: "Enter price per gallon" };
  if (p >= 20) return { ok: false, message: "Price looks wrong — enter $/gal like 4.459" };
  if (!/^\d{4}-\d{2}-\d{2}$/.test(input.occurredOn)) {
    return { ok: false, message: "Pick a date" };
  }
  const gallons = Math.round(g * 1000) / 1000;
  const priceMilliCents = Math.round(p * 100000);
  // priceMilliCents × gallons → milli-cents; ÷ 1000 → cents.
  const amountCents = Math.round((priceMilliCents * gallons) / 1000);
  const where = input.where.trim();
  return {
    ok: true,
    value: {
      gallons,
      priceMilliCents,
      amountCents,
      occurredOn: input.occurredOn,
      note: where === "" ? null : where,
    },
  };
}

/** Only the fields that actually changed — keeps the sync patch minimal. */
export function fuelPatch(
  row: FuelValues,
  next: FuelValues,
): Partial<FuelValues> {
  const out: Partial<FuelValues> = {};
  for (const k of ["gallons", "priceMilliCents", "amountCents", "occurredOn", "note"] as const) {
    if (row[k] !== next[k]) (out as Record<string, unknown>)[k] = next[k];
  }
  return out;
}

export function formatPrice(priceMilliCents: number | null): string {
  return priceMilliCents == null ? "" : (priceMilliCents / 100000).toFixed(3);
}
